// Polyfill Buffer for dependencies that expect the Node global in the browser.
import { Buffer } from "buffer";
(globalThis as unknown as { Buffer: typeof Buffer }).Buffer =
  (globalThis as unknown as { Buffer?: typeof Buffer }).Buffer ?? Buffer;

import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import "#app/global.css";
import { routeTree } from "#app/routeTree.gen";
import { RouteErrorBoundary } from "#app/lib/route-error-boundary";
import { captureOAuthReturn } from "#app/lib/oauth-return";

// BEFORE the router is created. Core's OAuth callback redirects to the app root
// with the outcome in the query string, and the root immediately redirects to
// the user's default route through `<Navigate>`, which drops search params — so
// the only place the outcome is still readable is here. See oauth-return.ts.
captureOAuthReturn();

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

// TanStack renders a bare default error boundary per route; point it at the
// styled one so runtime route errors match the app's chrome.
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, reset }) => (
    <RouteErrorBoundary error={error} retry={reset} />
  ),
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
